import React from "react";
import agricultureImg from "./agrilinkimages/india_agriculture_market.png";
import dairyImg from "./agrilinkimages/india_dairy_market.png";
import globalImg from "./agrilinkimages/global_agriculture_market.png";
import forecastImg from "./agrilinkimages/india_agriculture_forecast.png";

const globalReportData = {
  india: {
    agriculture: {
      marketSize: "INR 99,689 billion (2024)",
      projected: "INR 236,603.2 billion (2033)",
      CAGR: "10.08%",
      exports: "$51.9 billion (2024-25)",
      challenges: ["Climate variability", "Water scarcity", "Fragmented supply chains"],
    },
    dairy: {
      marketSize: "INR 18,975 billion (2024)",
      projected: "INR 57,001.81 billion (2033)",
      CAGR: "12.35%",
      exports: "$492.9 million (2024-25)",
      challenges: ["Quality control", "Supply chain inefficiencies", "Lack of traceability"],
    },
    fisheries: {
      marketSize: "$27.15 billion (2024)",
      projected: "$33.61 billion (2030)",
      exports: "$7.4 billion (2024-25)",
      challenges: ["Overfishing", "Inadequate cold storage", "Regulatory complexities"],
    },
    poultry: {
      marketSize: "INR 2,304 billion (2024)",
      projected: "INR 8,430 billion (2033)",
      CAGR: "12.6%",
      exports: "$8.98 million (2023)",
      challenges: ["Disease outbreaks", "Feed quality", "Biosecurity concerns"],
    },
  },
  global: {
    agriculture: "Projected to reach $12,122.26 billion by 2028, CAGR 6.6%",
    dairy: "Rising demand in emerging markets",
    fisheries: "Sustainable fishing & aquaculture key to global supply",
    poultry: "Growing protein consumption in developing countries",
  },
  challenges: [
    "Climate Change: Erratic weather patterns affecting crop yields and livestock health",
    "Supply Chain Inefficiencies: Post-harvest losses due to inadequate infrastructure",
    "Market Access: Limited access to global markets for small-scale producers",
    "Regulatory Hurdles: Complex regulations hindering smooth trade and compliance",
  ],
  digitalRole: [
    "Precision Agriculture: AI tools for soil health, pest detection, yield prediction",
    "Supply Chain Optimization: Blockchain for traceability, reducing post-harvest losses",
    "Farmer Engagement: Real-time market prices, weather updates, expert advice",
    "Data-Driven Decision Making: Analytics for resource allocation and risk management",
  ],
  ecosystem: [
    "Government: Policy frameworks supporting digital adoption and infrastructure",
    "Private Sector: Investment in agri-tech startups and innovation hubs",
    "Farmers: Training programs and access to digital tools",
    "Consumers: Transparency in sourcing and quality assurance",
  ],
};

const AgriLinkGlobalOutlook = () => {
  return (
    <div className="bg-gradient-to-b from-green-50 to-blue-50 min-h-screen px-4 sm:px-6 md:px-20 py-10 text-gray-800">
      {/* Hero */}
      <div className="text-center mb-10">
        <h1 className="text-2xl md:text-4xl font-bold text-green-800">
          🌱 AgriLink Global Outlook
        </h1>
        <p className="mt-4 text-gray-700 max-w-3xl mx-auto">
          Comprehensive insights on India’s Agriculture, Dairy, Fisheries, and Poultry sectors with domestic & global outlook, challenges, and opportunities for digital platforms.
        </p>
      </div>

      {/* Image Gallery */}
      <div className="grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-4 gap-4 mb-4">
        <img
          src={agricultureImg}
          alt="India Agriculture Market"
          className="rounded-lg shadow-md w-full"
        />
        <img
          src={dairyImg}
          alt="India Dairy Market"
          className="rounded-lg shadow-md w-full"
        />
        <img
          src={globalImg}
          alt="Global Agriculture Market"
          className="rounded-lg shadow-md w-full"
        />
        <img
          src={forecastImg}
          alt="India Agriculture Forecast"
          className="rounded-lg shadow-md w-full"
        />
      </div>

      {/* India Market Section */}
      <div className="mb-10">
        <h2 className="text-xl md:text-2xl font-bold text-green-700 mb-4">India: Sectoral Market Overview (2024–2033)</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {Object.entries(globalReportData.india).map(([sector, data], idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <h3 className="font-semibold text-green-800 text-lg mb-2">{sector.charAt(0).toUpperCase() + sector.slice(1)}</h3>
              <ul className="text-gray-700 text-sm space-y-1">
                {Object.entries(data).map(([key, value], i) => {
                  if (Array.isArray(value)) {
                    return (
                      <li key={i}>
                        <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>
                        <ul className="list-disc ml-5 mt-1">
                          {value.map((v, j) => (
                            <li key={j}>{v}</li>
                          ))}
                        </ul>
                      </li>
                    );
                  }
                  return (
                    <li key={i}>
                      <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Global Market Section */}
      <div className="mb-10">
        <h2 className="text-xl md:text-2xl font-bold text-green-700 mb-4">🌍 Global Outlook (2024–2034)</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {Object.entries(globalReportData.global).map(([sector, value], idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <h3 className="font-semibold text-green-800 text-lg mb-2">{sector.charAt(0).toUpperCase() + sector.slice(1)}</h3>
              <p className="text-gray-700 text-sm">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Challenges */}
      <div className="mb-10">
        <h2 className="text-xl md:text-2xl font-bold text-green-700 mb-4">🚧 Challenges Across Sectors</h2>
        <ul className="list-disc ml-5 text-gray-700 space-y-1">
          {globalReportData.challenges.map((c, idx) => (
            <li key={idx}>{c}</li>
          ))}
        </ul>
      </div>

      {/* Role of Digital Platforms */}
      <div className="mb-10">
        <h2 className="text-xl md:text-2xl font-bold text-green-700 mb-4">🤖 Role of Digital Platforms & AI</h2>
        <ul className="list-disc ml-5 text-gray-700 space-y-1">
          {globalReportData.digitalRole.map((d, idx) => (
            <li key={idx}>{d}</li>
          ))}
        </ul>
      </div>

      {/* Integrated Ecosystem */}
      <div className="mb-10">
        <h2 className="text-xl md:text-2xl font-bold text-green-700 mb-4">🔗 Integrated Ecosystem for Stakeholders</h2>
        <ul className="list-disc ml-5 text-gray-700 space-y-1">
          {globalReportData.ecosystem.map((e, idx) => (
            <li key={idx}>{e}</li>
          ))}
        </ul>
      </div>

      {/* Footer */}
      <footer className="bg-green-700 text-white py-8 px-4 sm:px-6 md:px-20 rounded-lg">
        <p className="text-center text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default AgriLinkGlobalOutlook;
